import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, MonitorIcon, CreditCardIcon, CheckIcon, UserIcon, DoorOpenIcon, ShieldCheckIcon, WalletIcon, BellIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};
const QR_PATTERN = [[1, 1, 1, 0, 1, 0, 1, 1, 1], [1, 0, 1, 1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 0, 1, 1, 1], [0, 0, 0, 1, 1, 1, 0, 0, 0], [1, 1, 0, 1, 0, 0, 1, 1, 0], [0, 1, 1, 0, 1, 1, 0, 1, 1], [1, 1, 1, 0, 0, 1, 1, 1, 1], [1, 0, 1, 1, 1, 0, 1, 0, 0], [1, 1, 1, 0, 1, 1, 1, 1, 1]];
function QrCode({
  size = 5
}: {
  size?: number;
}) {
  return <span className="grid grid-cols-9 gap-[2px]" style={{
    width: size * 9 + 16
  }} aria-hidden="true" data-id="element-1444" data-mp="1sucss4ltzypd">
      {QR_PATTERN.flatMap((row, y) => row.map((cell, x) => <span key={`${x}-${y}`} style={{
      width: size,
      height: size
    }} className={cell ? 'bg-[#121212]' : 'bg-transparent'} data-id="element-1445" data-mp="5e4pisw0x7lw" data-mpi={x} />))}
    </span>;
}
function BcitiWordmark({
  className = ''
}: {
  className?: string;
}) {
  return <span className={`inline-flex items-center gap-1.5 font-sans font-bold tracking-[0.06em] text-[#121212] ${className}`} data-id="element-1446" data-mp="t48pmqa06p85">
      BCITI
      <span className="w-[0.45em] h-[0.45em] bg-[#4F46E5] rounded-[1px]" aria-hidden="true" data-id="element-1447" data-mp="o6god5ngxqra" />
    </span>;
}
function ActiveBadge({
  isFR
}: {
  isFR: boolean;
}) {
  return <span className="inline-flex items-center gap-1.5 font-sans text-[9px] text-[#5B6478]" data-id="element-1448" data-mp="eejec28754u5">
      <span className="w-3 h-3 rounded-full bg-[#16A34A] flex items-center justify-center" data-id="element-1449" data-mp="1bvrrgtsajx8l">
        <CheckIcon className="w-2 h-2 text-white" strokeWidth={3.5} data-id="element-1450" data-mp="172yp2ckap0zo" />
      </span>
      {isFR ? 'Carte active' : 'Active card'}
    </span>;
}
function PhoneMockup({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="w-[128px] rounded-[20px] border-[3px] border-[#121212] bg-white p-2.5 shadow-[0_4px_16px_rgba(18,18,18,0.10)]" data-id="element-1451" data-mp="35c5mss2vt7pf">
      <span className="block w-8 h-1 rounded-full bg-[#121212] mx-auto mb-2.5" aria-hidden="true" data-id="element-1452" data-mp="2rjy3o34itf97" />
      <BcitiWordmark className="text-[13px]" data-id="element-1453" data-mp="2hnuuwef5yida" />
      <span className="flex justify-center my-3" data-id="element-1454" data-mp="2hn39txj70319">
        <QrCode size={5} data-id="element-1455" data-mp="2mmxv9lk780ex" />
      </span>
      <p className="font-sans text-[11px] font-medium text-[#121212] text-center" data-id="element-1456" data-mp="2mo0r87wdot1p">
        Marie Tremblay
      </p>
      <span className="flex justify-center mt-1.5" data-id="element-1457" data-mp="28or9v4wfrsw5">
        <ActiveBadge isFR={isFR} data-id="element-1458" data-mp="2dlzvjfgsmf25" />
      </span>
    </div>;
}
function WebMockup({
  isFR
}: {
  isFR: boolean;
}) {
  const rows = isFR ? ['Mes services', 'Ma famille', 'Mes propriétés', 'Mes abonnements'] : ['My services', 'My family', 'My properties', 'My subscriptions'];
  return <div className="relative w-[196px] pb-3" data-id="element-1459" data-mp="lylb0pdgve31">
      <div className="w-[176px] rounded-lg border border-[#E4E6EC] bg-white p-3 shadow-[0_2px_10px_rgba(18,18,18,0.06)]" data-id="element-1460" data-mp="lzmjqn70fohl">
        <BcitiWordmark className="text-[11px]" data-id="element-1461" data-mp="gxnipjyqt3nt" />
        <p className="font-sans text-[11px] font-semibold text-[#121212] leading-tight mt-2" data-id="element-1462" data-mp="ures8sch4njj">
          {isFR ? 'Bonjour' : 'Hello'}
          <br data-id="element-1463" data-mp="pyvcboqbdgbk" />
          Marie Tremblay
        </p>
        <ul className="mt-2.5 space-y-1.5" data-id="element-1464" data-mp="14ot8oryedcbu">
          {rows.map(row => <li key={row} className="flex items-center gap-1.5 rounded bg-[#F6F7F9] px-2 py-1.5 font-sans text-[9px] text-[#5B6478]" data-id="element-1465" data-mp="zq87oqbbmomc">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-1466" data-mp="1dp7udsf9wtfv" />
              {row}
            </li>)}
        </ul>
      </div>
      <span className="absolute -bottom-1 left-2 w-[184px] h-1.5 rounded-b-md bg-[#E4E6EC]" aria-hidden="true" data-id="element-1467" data-mp="3fjgnvf778dnp" />
      <div className="absolute right-0 bottom-2 w-[78px] rounded-[12px] border-2 border-[#121212] bg-white p-1.5 shadow-[0_4px_14px_rgba(18,18,18,0.12)]" data-id="element-1468" data-mp="3cxh8zg0xvgtc">
        <BcitiWordmark className="text-[8px]" data-id="element-1469" data-mp="3hu7s6c81isiz" />
        <span className="flex justify-center my-1.5" data-id="element-1470" data-mp="3lx49lzlugilc">
          <QrCode size={3} data-id="element-1471" data-mp="3lvjg8db9n5p2" />
        </span>
        <p className="font-sans text-[7px] font-medium text-[#121212] text-center" data-id="element-1472" data-mp="3qx8s1v6bbne7">
          Marie Tremblay
        </p>
      </div>
    </div>;
}
function PrintedCardMockup() {
  return <div className="w-[176px] rotate-[-4deg] rounded-xl border border-[#E7E8F0] bg-white px-4 py-4 shadow-[0_4px_16px_rgba(18,18,18,0.08)]" data-id="element-1473" data-mp="2y7s3j8ltcqn9">
      <BcitiWordmark className="text-[15px]" data-id="element-1474" data-mp="335lbo76ofuq6" />
      <span className="flex justify-center my-3" data-id="element-1475" data-mp="37xl4zgs8rpds">
        <QrCode size={5} data-id="element-1476" data-mp="37zfs91qzjtl1" />
      </span>
      <p className="font-sans text-[11px] font-medium text-[#121212] text-center" data-id="element-1477" data-mp="2aih1zown2s82">
        Marie Tremblay
      </p>
      <p className="font-sans text-[9px] text-[#5B6478] text-center tracking-[0.14em] mt-0.5" data-id="element-1478" data-mp="2fhadjbsd895l">
        100 246 330
      </p>
    </div>;
}
const ACCESS_METHODS: {
  id: string;
  icon: typeof SmartphoneIcon;
  title: Bilingual;
  line: Bilingual;
  capabilities: Bilingual[];
  Mockup: (props: {
    isFR: boolean;
  }) => JSX.Element;
}[] = [{
  id: 'mobile',
  icon: SmartphoneIcon,
  title: {
    en: 'Mobile app',
    fr: 'App mobile'
  },
  line: {
    en: 'Your card, always with you.',
    fr: 'Votre carte toujours avec vous.'
  },
  capabilities: [{
    en: 'Apple Wallet',
    fr: 'Apple Wallet'
  }, {
    en: 'QR / NFC',
    fr: 'QR / NFC'
  }, {
    en: 'Services',
    fr: 'Services'
  }, {
    en: 'Notifications',
    fr: 'Notifications'
  }],
  Mockup: PhoneMockup
}, {
  id: 'web',
  icon: MonitorIcon,
  title: {
    en: 'Responsive web',
    fr: 'Web adaptatif'
  },
  line: {
    en: 'No app required.',
    fr: 'Aucune application requise.'
  },
  capabilities: [{
    en: 'Secure access',
    fr: 'Accès sécurisé'
  }, {
    en: 'Phone, tablet or computer',
    fr: 'Téléphone, tablette ou ordinateur'
  }, {
    en: 'Always up to date',
    fr: 'Toujours à jour'
  }],
  Mockup: WebMockup
}, {
  id: 'printed',
  icon: CreditCardIcon,
  title: {
    en: 'Printed card',
    fr: 'Carte imprimée'
  },
  line: {
    en: 'For situations that require it.',
    fr: 'Pour les usages qui l’exigent.'
  },
  capabilities: [{
    en: 'QR',
    fr: 'QR'
  }, {
    en: 'Proof of identity or entitlement',
    fr: 'Preuve d’identité ou d’admissibilité'
  }, {
    en: 'Access to selected services',
    fr: 'Accès à certains services'
  }],
  Mockup: () => <PrintedCardMockup data-id="element-1479" data-mp="10m598qlg9nt4" />
}];
const OUTCOMES: {
  icon: typeof DoorOpenIcon;
  title: Bilingual;
  body: Bilingual;
}[] = [{
  icon: DoorOpenIcon,
  title: {
    en: 'Access',
    fr: 'Accéder'
  },
  body: {
    en: 'Services, facilities, programs',
    fr: 'Services, installations, programmes'
  }
}, {
  icon: ShieldCheckIcon,
  title: {
    en: 'Prove',
    fr: 'Prouver'
  },
  body: {
    en: 'Residency, membership, entitlement or status',
    fr: 'Résidence, adhésion, droit ou statut'
  }
}, {
  icon: WalletIcon,
  title: {
    en: 'Transact',
    fr: 'Effectuer des transactions'
  },
  body: {
    en: 'Payments, registrations, benefits',
    fr: 'Paiements, inscriptions, avantages'
  }
}, {
  icon: BellIcon,
  title: {
    en: 'Receive',
    fr: 'Recevoir'
  },
  body: {
    en: 'Communications and personalized services',
    fr: 'Communications et services personnalisés'
  }
}];
const OPTIONAL_CAPABILITIES: Bilingual[] = [{
  en: 'NFC',
  fr: 'NFC'
}, {
  en: 'Wallet',
  fr: 'Portefeuille numérique'
}, {
  en: 'Membership validation',
  fr: 'Validation d’adhésion'
}, {
  en: 'Residency verification',
  fr: 'Vérification de résidence'
}, {
  en: 'Subscriptions',
  fr: 'Abonnements'
}, {
  en: 'Discount programs',
  fr: 'Programmes de rabais'
}, {
  en: 'Loyalty programs',
  fr: 'Programmes de fidélité'
}, {
  en: 'Family accounts',
  fr: 'Comptes familiaux'
}];
export function AccessMethods() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <div className="max-w-[1320px] mx-auto" data-id="element-1480" data-mp="1c6z8jo5373pb">
      <motion.div {...reveal} className="text-center max-w-3xl mx-auto" data-id="element-1481" data-mp="1gyh7y7rgow86">
        <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.12] tracking-tight" data-id="element-1482" data-mp="1gzsci9sqe80c">
          {isFR ? 'Simple pour les usagers. Puissant pour les organisations.' : 'Simple for users. Powerful for organizations.'}
        </h2>
        <p className="font-sans text-lg text-[#5B6478] mt-4" data-id="element-1483" data-mp="1vwt7cm67yj31">
          {isFR ? 'Votre identité, accessible comme vous le voulez.' : 'Your identity, accessible your way.'}
        </p>
      </motion.div>

      {/* Three access methods */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.08,
      ease: EASE
    }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-14" data-id="element-1484" data-mp="5jc0yrkasbir">
        {ACCESS_METHODS.map(({
        id,
        icon: Icon,
        title,
        line,
        capabilities,
        Mockup
      }) => <article key={id} className="rounded-2xl border border-[#ECEDF2] bg-white p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-1485" data-mp="3kfdszhgasheu">
              <div className="shrink-0 flex justify-center w-full sm:w-auto" data-id="element-1486" data-mp="3gbzewwbx6b41">
                <Mockup isFR={isFR} data-id="element-1487" data-mp="gxvuw1qbuorm" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-1488" data-mp="bzcaw6vann1w">
                <span className="w-11 h-11 rounded-xl bg-[#F5F5FB] flex items-center justify-center" data-id="element-1489" data-mp="bxhlogczp8s0">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1490" data-mp="7ztpzb3h3bz2" />
                </span>
                <h3 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#121212] mt-4" data-id="element-1491" data-mp="2sm52ugzfcqku">
                  {pick(title)}
                </h3>
                <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1.5" data-id="element-1492" data-mp="n7gov6vten19">
                  {pick(line)}
                </p>
                <ul className="mt-4 pt-4 border-t border-[#F0F1F5] space-y-2.5" data-id="element-1493" data-mp="i5r8rctqjj6b">
                  {capabilities.map(capability => <li key={capability.en} className="flex items-start gap-2.5 font-sans text-[15px] text-[#121212] leading-snug" data-id="element-1494" data-mp="r80iw1l3daj6">
                      <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-1" strokeWidth={2.25} aria-hidden="true" data-id="element-1495" data-mp="10yv6b9a2qg67" />
                      {pick(capability)}
                    </li>)}
                </ul>
              </div>
            </article>)}
      </motion.div>

      {/* Connectors → one identity */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.1,
      ease: EASE
    }} className="relative" data-id="element-1496" data-mp="3cpfludre64lo">
        <svg viewBox="0 0 1000 70" preserveAspectRatio="none" className="hidden xl:block w-full h-[70px]" aria-hidden="true" data-id="element-1497" data-mp="36l1bk4mrq12f">
          <path d="M167 4 L167 26 L500 44" fill="none" stroke="#4F46E5" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-1498" data-mp="2srs263ytlfjw" />
          <path d="M500 4 L500 66" fill="none" stroke="#4F46E5" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-1499" data-mp="2nvkx0de90830" />
          <path d="M833 4 L833 26 L500 44" fill="none" stroke="#4F46E5" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-1500" data-mp="83nkzpfq9sta" />
          {[167, 500, 833].map(x => <circle key={x} cx={x} cy="5" r="3.5" fill="#4F46E5" data-id="element-1501" data-mp="6mhzpspdr026" />)}
        </svg>

        <span className="xl:hidden block w-px h-10 bg-[#4F46E5]/40 mx-auto" aria-hidden="true" data-id="element-1502" data-mp="1ndpcyqpywa9" />

        <div className="flex justify-center xl:-mt-1" data-id="element-1503" data-mp="3su85m3tzw7bx">
          <div className="w-full max-w-xl rounded-2xl border border-[#E4E4F6] bg-[#F7F7FD] px-6 py-5 flex items-center gap-5" data-id="element-1504" data-mp="3svszdr8hjm8q">
            <span className="w-14 h-14 shrink-0 rounded-xl bg-white flex items-center justify-center" data-id="element-1505" data-mp="pd81fe37sgtg">
              <UserIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1506" data-mp="klpywlljm86d" />
            </span>
            <div className="min-w-0" data-id="element-1507" data-mp="flbxvniczyhu">
              <h3 className="font-sans text-base font-semibold tracking-[0.06em] uppercase text-[#4F46E5]" data-id="element-1508" data-mp="1d2u1s4vbp231">
                {isFR ? 'Une identité BCITI | ATNA' : 'One BCITI | ATNA identity'}
              </h3>
              <p className="font-sans text-sm text-[#5B6478] mt-1.5" data-id="element-1509" data-mp="1fp33xjv8trze">
                {isFR ? 'Profil · Relations · Droits · Consentements · Attestations' : 'Profile · Relationships · Rights · Consents · Credentials'}
              </p>
              <p className="font-sans text-sm text-[#121212] mt-1.5" data-id="element-1510" data-mp="1ul0wks5yke6v">
                {isFR ? 'La même identité, partout dans l’écosystème.' : 'The same identity, everywhere in the ecosystem.'}
              </p>
            </div>
          </div>
        </div>

        <span className="block w-px h-9 bg-[#4F46E5]/40 mx-auto" aria-hidden="true" data-id="element-1511" data-mp="28cyu05qb4ln7" />
      </motion.div>

      {/* What the identity enables */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.12,
      ease: EASE
    }} className="rounded-2xl border border-[#ECEDF2] bg-white px-6 py-7 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7 xl:gap-0 xl:divide-x xl:divide-[#F0F1F5]" data-id="element-1512" data-mp="2iad8w0wl12de">
        {OUTCOMES.map(({
        icon: Icon,
        title,
        body
      }) => <div key={title.en} className="flex items-start gap-4 xl:px-7 first:xl:pl-0" data-id="element-1513" data-mp="3f0p4d24q9oeq">
            <span className="w-11 h-11 shrink-0 rounded-full bg-[#F5F5FB] flex items-center justify-center" data-id="element-1514" data-mp="3sug79rnyea1h">
              <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1515" data-mp="311fsufrgvih3" />
            </span>
            <div data-id="element-1516" data-mp="2w479wcyhvaes">
              <h3 className="font-sans text-base font-semibold text-[#121212]" data-id="element-1517" data-mp="2w4p8vhcjk2qe">
                {pick(title)}
              </h3>
              <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1" data-id="element-1518" data-mp="352j40oykkqbz">
                {pick(body)}
              </p>
            </div>
          </div>)}
      </motion.div>

      {/* Optional capabilities */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.14,
      ease: EASE
    }} className="mt-12 text-center" data-id="element-1519" data-mp="kc0jlean8l2q">
        <p className="font-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-1520" data-mp="wvfpp24a0wgq">
          {isFR ? 'Capacités optionnelles' : 'Optional capabilities'}
        </p>
        <div className="flex flex-wrap justify-center gap-2.5 mt-5" data-id="element-1521" data-mp="ixzgxjyicqlt">
          {OPTIONAL_CAPABILITIES.map(capability => <span key={capability.en} className="rounded-lg border border-[#ECEDF2] px-4 py-2 font-sans text-sm text-[#5B6478]" data-id="element-1522" data-mp="1bkk4e9bv4ik3">
              {pick(capability)}
            </span>)}
        </div>
      </motion.div>

      {/* Closing */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.16,
      ease: EASE
    }} className="text-center mt-14" data-id="element-1523" data-mp="16tlo39qvjkgu">
        <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-1524" data-mp="1uieekqoq9042">
          {isFR ? 'Une identité. Plusieurs façons d’y accéder.' : 'One identity. Multiple ways to access it.'}
        </p>
        <p className="font-sans text-lg text-[#5B6478] mt-3" data-id="element-1525" data-mp="293sfr88ayxqq">
          {isFR ? 'Une expérience simple pour l’usager, une fondation réutilisable pour l’organisation.' : 'A simple experience for users. A reusable foundation for organizations.'}
        </p>
        <div className="w-14 h-[2px] bg-[#4F46E5] mx-auto mt-7" aria-hidden="true" data-id="element-1526" data-mp="1v5u6gh8u9ri3" />
      </motion.div>
    </div>;
}